// Demo storyboard for visual review. Hardcoded fake data covering all 5 templates
// in narrative order. Cycle button in App.tsx steps through it.

import type { Storyboard } from '../types';

export const DEMO_STORYBOARDS: Storyboard[] = [
  {
    title: 'Studio overview',
    scenes: [
      {
        id: 's1',
        topology: 'hero',
        content: {
          eyebrow: 'DIGICRAFTIQUE',
          headline: 'On-prem AI for regulated domains.',
          body: 'Local-first, audit-grounded, deterministic. Built for teams who can\'t hand their data to a vendor.',
        },
        spoken_script: 'There is a shape to this. Let me draw it.',
      },
      {
        id: 's2',
        topology: 'collection',
        content: {
          eyebrow: 'IN PRODUCTION',
          headline: 'Four projects. One discipline.',
          items: [
            { eyebrow: 'TAX LAW',     headline: 'CBIC RAG',    body: 'Quote-grounded retrieval over 14,925 Indian tax documents at 95% recall.' },
            { eyebrow: 'CLAIMS',      headline: 'OpenClaw',    body: 'Insurance claim ops automation with deterministic decision rails.' },
            { eyebrow: 'RECRUITING',  headline: 'Job Agent',   body: 'Researcher discovery and pre-vetting from public signals.' },
            { eyebrow: 'INTEL',       headline: 'Pharma CI',   body: 'Daily competitive intelligence over 30+ sources, scored and deduplicated.' },
          ],
        },
        spoken_script: 'Four flagship projects in production today.',
      },
      {
        id: 's3',
        topology: 'sequence',
        content: {
          eyebrow: 'HOW CBIC RAG WORKS',
          headline: 'Three stages. The middle one is where we earned 95%.',
          items: [
            { headline: 'Parse',  body: 'Strip 14,925 PDFs into structured passages with citation pointers.' },
            { headline: 'Chunk',  body: 'Section-aware splitting that respects statute boundaries and clause depth.' },
            { headline: 'Embed',  body: 'Hybrid BM25 + dense retrieval with cross-encoder reranking on the hot tail.' },
          ],
        },
        spoken_script: 'Parse, then chunk, then embed. The chunker is where the recall lift came from.',
      },
      {
        id: 's4',
        topology: 'dichotomy',
        content: {
          items: [
            {
              eyebrow:  'MOST AI VENDORS',
              headline: 'Pipe a prompt to an API.',
              points: [
                'Your data leaves your network.',
                'No grounding, no audit trail.',
                'You\'re renting an opinion.',
              ],
            },
            {
              eyebrow:  'WHAT WE BUILD',
              headline: 'Production systems on infrastructure you control.',
              points: [
                'Local inference, your hardware.',
                'Every claim cites a source byte-offset.',
                'You own the model weights.',
              ],
            },
          ],
        },
        spoken_script: 'Most wrappers fail here. We do not.',
      },
      {
        id: 's5',
        topology: 'quote',
        content: {
          mode: 'stat',
          eyebrow: 'INDEXED IN CBIC RAG',
          headline: '14,925',
          body: 'Indian tax documents — every paragraph cited back to its source statute.',
          citation: 'Live as of April 2026',
        },
        spoken_script: 'Fourteen thousand nine hundred twenty-five documents. Every claim backed by a citation.',
      },
      {
        id: 's6',
        topology: 'stat-panel',
        content: {
          eyebrow: 'CBIC RAG — BY THE NUMBERS',
          headline: 'What "production-grade" looks like.',
          stats: [
            { value: '15,776', label: 'Documents indexed' },
            { value: '0.96',   label: 'Adjusted recall@10' },
            { value: '22 ch/s', label: 'Embed throughput' },
            { value: '7 GPU',  label: 'All AMD, all Vulkan' },
            { value: '$0',     label: 'API spend / month' },
          ],
        },
        spoken_script: 'Five numbers that matter. Zero dollars to a vendor — that is the whole point.',
      },
      {
        id: 's7',
        topology: 'architecture',
        content: {
          eyebrow: 'CBIC RAG — DATA FLOW',
          headline: 'Three phases. The classifier in phase one is what made it work.',
          stages: [
            {
              label: 'Ingest',
              nodes: [
                { id: 'pdfs', label: '15,776 CBIC PDFs', sublabel: 'tax statutes, circulars, FAQs' },
                { id: 'classifier', label: 'Pass-1 classifier', sublabel: 'qwen3-14b on GPU 2' },
                { id: 'plan', label: 'Chunking plan', sublabel: 'doc_type + section_regex + table_regions' },
              ],
            },
            {
              label: 'Index',
              nodes: [
                { id: 'chunker', label: 'Pass-2 chunker', sublabel: 'deterministic, section-aware' },
                { id: 'embed-pool', label: 'Embed pool', sublabel: '6 AMD GPUs, BGE-M3, 22 ch/s' },
                { id: 'qdrant', label: 'Qdrant vectors', sublabel: '1024-dim dense, hybrid + sparse' },
              ],
            },
            {
              label: 'Query',
              nodes: [
                { id: 'retrieve', label: 'Hybrid retrieve', sublabel: 'BM25 + dense, top-10' },
                { id: 'rerank', label: 'Cross-encoder rerank', sublabel: 'BGE-reranker-v2-m3' },
                { id: 'answer', label: 'Quote-grounded answer', sublabel: 'qwen3-14b, per-claim attribution' },
              ],
            },
          ],
        },
        spoken_script: 'Ingest, index, query. The trick was teaching the chunker to respect statute structure before it ever embedded a token.',
      },
      {
        id: 's8',
        topology: 'tech-stack',
        content: {
          eyebrow: 'THE STACK',
          headline: 'Open-source, on-prem, AMD-only.',
          body: 'Every component runs locally. No cloud APIs in the production path.',
          chips: [
            { name: 'Qdrant',         version: '1.11', group: 'Storage' },
            { name: 'SQLite',         version: '3.46', group: 'Storage' },
            { name: 'BGE-M3',         version: 'large', group: 'Embedding' },
            { name: 'Qwen3-14B',      version: 'Q4_K_M', group: 'Inference' },
            { name: 'BGE-reranker-v2', version: 'm3',    group: 'Inference' },
            { name: 'FastAPI',        version: '0.115', group: 'API' },
            { name: 'Vulkan',         version: 'AMD',   group: 'Runtime' },
            { name: 'llama.cpp',      version: 'b8840', group: 'Runtime' },
          ],
        },
        spoken_script: 'Eight components. All open. All local. All running on AMD silicon.',
      },
      {
        id: 's9',
        topology: 'profile',
        content: {
          eyebrow: 'PARTNER · BUILDER · STUDIO LEAD',
          headline: 'Rahul Goyanka',
          body: 'I started DigiCraftique because I kept seeing teams hand their proprietary data to AI vendors and lose control of their own ground truth. The studio works with a handful of clients per quarter on systems that run on infrastructure they own — legal-grade retrieval, claim-ops automation, competitive intelligence. Engineering and human-centered design at the intersection.',
          citation: 'RG, founder',
        },
        spoken_script: 'I am Rahul. RG. The studio is one person by design — I do the architecture, the code, and the conversations.',
      },
      {
        id: 's10',
        topology: 'contact',
        content: {
          eyebrow: 'GET IN TOUCH',
          headline: 'Tell me what you\'re chasing.',
          body: 'I read every message. Reply within 48 hours.',
          items: [
            { eyebrow: 'EMAIL',     headline: 'rg@digicraftique.co',                body: 'mailto:rg@digicraftique.co' },
            { eyebrow: 'BOOK',      headline: '20 minutes with RG',                 body: '#booking' },
            { eyebrow: 'WRITE-UPS', headline: 'Recent posts on the studio\'s blog', body: '/blog.html' },
          ],
        },
        spoken_script: 'Three ways in. Email is fastest; the call is for when there is something specific.',
      },
      {
        id: 's11',
        topology: 'booking',
        content: {
          eyebrow: 'BOOK A 20-MIN CALL',
          headline: 'Pick a slot. RG joins from a quiet room.',
          body: 'Mon / Wed / Fri at 14:00 or 16:00 IST. No agenda needed — show up with your problem.',
        },
        spoken_script: 'Pick a slot that works. Twenty minutes. Bring the actual problem.',
      },
      {
        id: 's12',
        topology: 'welcome',
        content: {
          eyebrow: 'DIGICRAFTIQUE.CO',
          headline: "Hi. I'm Data.",
          body: 'The studio is open. Speak or type — what brought you here?',
        },
        spoken_script: 'Hello. The studio is open. Tell me what you are chasing.',
      },
    ],
  },
];
